import { useState, useEffect } from 'react'

interface PriceData {
  currentPrice: string
  comparePrice: string
  isLoading: boolean
}

const PRICE_MAP: Record<string, { current: string; compare: string }> = {
  US: { current: '$14.90', compare: '$34.90' },
  EC: { current: '$14.90', compare: '$34.90' },
  MX: { current: '$327.12 MXN', compare: '$750.00 MXN' },
  CA: { current: '$23.10 CAD', compare: '$49.00 CAD' },
  CL: { current: '$17.216 CLP', compare: '$38.500 CLP' },
  CO: { current: '$57.667 COP', compare: '$125.000 COP' },
  PE: { current: 'S/ 55.00', compare: 'S/ 120.00' },
  UY: { current: '$637.00 UYU', compare: '$1.450 UYU' },
  PY: { current: 'Gs. 101.459', compare: 'Gs. 230.000' },
  BO: { current: 'Bs 109.00', compare: 'Bs 240.00' },
  AR: { current: 'ARS\xA0$14.990', compare: 'ARS\xA0$30.990' },
}

const DEFAULT_PRICES = PRICE_MAP.US

let cachedCountry: string | null = null

// Timezone → country mapping for Latin America
const TIMEZONE_TO_COUNTRY: Record<string, string> = {
  'America/Argentina/Buenos_Aires': 'AR',
  'America/Argentina/Cordoba': 'AR',
  'America/Argentina/Salta': 'AR',
  'America/Argentina/Jujuy': 'AR',
  'America/Argentina/Tucuman': 'AR',
  'America/Argentina/Catamarca': 'AR',
  'America/Argentina/La_Rioja': 'AR',
  'America/Argentina/San_Juan': 'AR',
  'America/Argentina/Mendoza': 'AR',
  'America/Argentina/San_Luis': 'AR',
  'America/Argentina/Rio_Gallegos': 'AR',
  'America/Argentina/Ushuaia': 'AR',
  'America/Mexico_City': 'MX',
  'America/Cancun': 'MX',
  'America/Merida': 'MX',
  'America/Monterrey': 'MX',
  'America/Matamoros': 'MX',
  'America/Mazatlan': 'MX',
  'America/Chihuahua': 'MX',
  'America/Ojinaga': 'MX',
  'America/Hermosillo': 'MX',
  'America/Tijuana': 'MX',
  'America/Bahia_Banderas': 'MX',
  'America/Santiago': 'CL',
  'America/Punta_Arenas': 'CL',
  'Pacific/Easter': 'CL',
  'America/Bogota': 'CO',
  'America/Lima': 'PE',
  'America/Montevideo': 'UY',
  'America/Asuncion': 'PY',
  'America/La_Paz': 'BO',
  'America/Guayaquil': 'EC',
  'Pacific/Galapagos': 'EC',
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'America/Edmonton': 'CA',
  'America/Winnipeg': 'CA',
  'America/Halifax': 'CA',
  'America/St_Johns': 'CA',
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'America/Phoenix': 'US',
  'America/Anchorage': 'US',
  'Pacific/Honolulu': 'US',
}

// Language/locale → country fallback
const LANG_TO_COUNTRY: Record<string, string> = {
  'es-AR': 'AR',
  'es-MX': 'MX',
  'es-CL': 'CL',
  'es-CO': 'CO',
  'es-PE': 'PE',
  'es-UY': 'UY',
  'es-PY': 'PY',
  'es-BO': 'BO',
  'es-EC': 'EC',
  'en-US': 'US',
  'en-CA': 'CA',
  'fr-CA': 'CA',
}

function detectByTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    return TIMEZONE_TO_COUNTRY[tz] || ''
  } catch {
    return ''
  }
}

function detectByLanguage(): string {
  try {
    const langs = navigator.languages || [navigator.language]
    for (const lang of langs) {
      const match = LANG_TO_COUNTRY[lang]
      if (match) return match
    }
    // Try just the region subtag
    for (const lang of langs) {
      const parts = lang.split('-')
      if (parts[1]) {
        const region = parts[1].toUpperCase()
        if (PRICE_MAP[region]) return region
      }
    }
    return ''
  } catch {
    return ''
  }
}

async function fetchWithTimeout(url: string, ms: number): Promise<Response> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), ms)
  try {
    const res = await fetch(url, { signal: controller.signal })
    return res
  } finally {
    clearTimeout(timer)
  }
}

async function detectByIP(): Promise<string> {
  const apis = [
    {
      url: 'https://ipwho.is/',
      extract: (data: any) => data.country_code,
    },
    {
      url: 'https://ip-api.com/json/?fields=countryCode',
      extract: (data: any) => data.countryCode,
    },
    {
      url: 'https://ipapi.co/json/',
      extract: (data: any) => data.country_code,
    },
  ]

  for (const api of apis) {
    try {
      const response = await fetchWithTimeout(api.url, 3000)
      if (!response.ok) continue
      const data = await response.json()
      const code = api.extract(data)?.toUpperCase()
      if (code && code.length === 2) return code
    } catch {
      continue
    }
  }

  return ''
}

async function detectCountry(): Promise<string> {
  // 1. Try timezone first (instant, no network)
  const tzCountry = detectByTimezone()
  if (tzCountry && PRICE_MAP[tzCountry]) return tzCountry

  // 2. Try language/locale (instant, no network)
  const langCountry = detectByLanguage()
  if (langCountry && PRICE_MAP[langCountry]) return langCountry

  // 3. Try IP-based APIs (network dependent)
  const ipCountry = await detectByIP()
  if (ipCountry) return ipCountry

  return ''
}

export function useGeoPricing(): PriceData {
  const [prices, setPrices] = useState<{ current: string; compare: string }>(
    () => {
      // Try instant detection on first render
      const tz = detectByTimezone()
      if (tz && PRICE_MAP[tz]) return PRICE_MAP[tz]
      const lang = detectByLanguage()
      if (lang && PRICE_MAP[lang]) return PRICE_MAP[lang]
      return DEFAULT_PRICES
    },
  )
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (cachedCountry) {
      setPrices(PRICE_MAP[cachedCountry] || DEFAULT_PRICES)
      setIsLoading(false)
      return
    }

    detectCountry().then((countryCode) => {
      cachedCountry = countryCode
      setPrices(PRICE_MAP[countryCode] || DEFAULT_PRICES)
      setIsLoading(false)
    })
  }, [])

  return {
    currentPrice: prices.current,
    comparePrice: prices.compare,
    isLoading,
  }
}
